import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  MoreVertical,
  Edit2,
  Trash2,
  Eye,
  User,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientBeliefSupportWithRelations,
  GetSupportingBeliefsInputInterface,
} from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import './SupportingBeliefsListView.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

interface SupportingBeliefsListViewProps {
  filters?: {
    userId?: number;
    goeUserId?: number;
    loeUserId?: number;
    beliefId?: number;
    supportingBeliefId?: number;
    cacheThoughtId?: number;
    cachePosition?: string;
    cacheSupportingThoughtId?: number;
    cacheSupportingPosition?: string;
    createdAfter?: string;
    createdBefore?: string;
    updatedAfter?: string;
    updatedBefore?: string;
  };
  showCheckboxes?: boolean;
  showBulkActions?: boolean;
  title?: string;
  compact?: boolean;
  limit?: number;
  includeBelief?: boolean;
  includeSupportingBelief?: boolean;
  includeThoughts?: boolean;
}

const SupportingBeliefsListView: React.FC<SupportingBeliefsListViewProps> = ({
  filters = {},
  showCheckboxes = true,
  showBulkActions = true,
  title,
  compact = false,
  limit,
  includeBelief = true,
  includeSupportingBelief = true,
  includeThoughts = true,
}) => {
  const navigate = useNavigate();

  const [supportingBeliefs, setSupportingBeliefs] = useState<ClientBeliefSupportWithRelations[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSupportingBeliefs, setSelectedSupportingBeliefs] = useState<Set<string>>(
    new Set()
  );
  const [totalSupportingBeliefs, setTotalSupportingBeliefs] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Page history stack for cursor-based pagination
  const [pageHistory, setPageHistory] = useState<Array<{ cursor: string | null; pageNum: number }>>(
    [{ cursor: null, pageNum: 1 }]
  );

  // Ordering state
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [orderDirection, setOrderDirection] = useState<string>('DESC');

  const itemsPerPage = limit || (compact ? 10 : 20);

  // Fetch supporting beliefs when component mounts or when filters/ordering changes
  useEffect(() => {
    fetchSupportingBeliefs('first', undefined, true);
  }, [filters, orderBy, orderDirection]);

  // Function to fetch supporting beliefs from API with cursor-based pagination
  const fetchSupportingBeliefs = async (
    direction: 'first' | 'next' | 'previous' = 'first',
    cursor?: string,
    shouldGetCount: boolean = false
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const apiParams: GetSupportingBeliefsInputInterface = {
        limit: itemsPerPage,
        orderBy,
        orderDirection,
        getCount: shouldGetCount,
        includeBelief,
        includeSupportingBelief,
        includeThoughts,
      };

      // Add cursor parameters based on direction and order
      if (direction === 'next' && cursor) {
        if (orderBy === 'createdAt') {
          if (orderDirection === 'DESC') {
            apiParams.beforeCreatedDate = cursor;
          } else {
            apiParams.afterCreatedDate = cursor;
          }
        } else if (orderBy === 'updatedAt') {
          if (orderDirection === 'DESC') {
            apiParams.beforeUpdatedDate = cursor;
          } else {
            apiParams.afterUpdatedDate = cursor;
          }
        }
      } else if (direction === 'previous' && cursor) {
        if (orderBy === 'createdAt') {
          if (orderDirection === 'DESC') {
            apiParams.afterCreatedDate = cursor;
          } else {
            apiParams.beforeCreatedDate = cursor;
          }
        } else if (orderBy === 'updatedAt') {
          if (orderDirection === 'DESC') {
            apiParams.afterUpdatedDate = cursor;
          } else {
            apiParams.beforeUpdatedDate = cursor;
          }
        }
      }

      // Add filter parameters from props
      if (filters.userId) {
        apiParams.userId = filters.userId;
      }
      if (filters.goeUserId) {
        apiParams.goeUserId = filters.goeUserId;
      }
      if (filters.loeUserId) {
        apiParams.loeUserId = filters.loeUserId;
      }
      if (filters.beliefId) {
        apiParams.beliefId = filters.beliefId;
      }
      if (filters.supportingBeliefId) {
        apiParams.supportingBeliefId = filters.supportingBeliefId;
      }
      if (filters.cacheThoughtId) {
        apiParams.cacheThoughtId = filters.cacheThoughtId;
      }
      if (filters.cachePosition) {
        apiParams.cachePosition = filters.cachePosition as 'believe' | 'disbelieve' | 'unsure';
      }
      if (filters.cacheSupportingThoughtId) {
        apiParams.cacheSupportingThoughtId = filters.cacheSupportingThoughtId;
      }
      if (filters.cacheSupportingPosition) {
        apiParams.cacheSupportingPosition = filters.cacheSupportingPosition as
          | 'believe'
          | 'disbelieve'
          | 'unsure';
      }
      if (filters.createdAfter) {
        apiParams.createdAfter = filters.createdAfter;
      }
      if (filters.createdBefore) {
        apiParams.createdBefore = filters.createdBefore;
      }
      if (filters.updatedAfter) {
        apiParams.updatedAfter = filters.updatedAfter;
      }
      if (filters.updatedBefore) {
        apiParams.updatedBefore = filters.updatedBefore;
      }

      const response = await adminApi.getSupportingBeliefs(apiParams);

      // Check if no results and show alert for navigation attempts
      if (
        response.supporting_beliefs.length === 0 &&
        (direction === 'next' || direction === 'previous')
      ) {
        alert('No results');
        return;
      }

      setSupportingBeliefs(response.supporting_beliefs);

      // Only update total count if it was requested and returned
      if (shouldGetCount && response.total !== undefined) {
        setTotalSupportingBeliefs(response.total);
      }

      // Update page history and current page based on direction
      if (direction === 'first') {
        // Reset to first page
        setCurrentPage(1);
        setPageHistory([{ cursor: null, pageNum: 1 }]);
      } else if (direction === 'next' && response.supporting_beliefs.length > 0) {
        // Add current page to history and move to next page
        const nextPageNum = currentPage + 1;
        const lastItem = response.supporting_beliefs[response.supporting_beliefs.length - 1];
        const nextCursor =
          orderBy === 'createdAt'
            ? lastItem.createdAt.toISOString()
            : lastItem.updatedAt.toISOString();

        setCurrentPage(nextPageNum);
        setPageHistory((prev) => [...prev, { cursor: nextCursor, pageNum: nextPageNum }]);
      } else if (direction === 'previous') {
        // Move back in history
        setPageHistory((prev) => {
          const newHistory = [...prev];
          newHistory.pop(); // Remove current page
          const previousPage = newHistory[newHistory.length - 1];
          setCurrentPage(previousPage.pageNum);
          return newHistory;
        });
      }
    } catch (err: any) {
      console.error('Error fetching supporting beliefs:', err);
      setError(err.message || 'Failed to fetch supporting beliefs');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSupportingBelief = (key: string): void => {
    setSelectedSupportingBeliefs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleSelectAll = (): void => {
    if (
      selectedSupportingBeliefs.size === supportingBeliefs.length &&
      supportingBeliefs.length > 0
    ) {
      setSelectedSupportingBeliefs(new Set());
    } else {
      setSelectedSupportingBeliefs(
        new Set(
          supportingBeliefs.map((sb) => `${sb.user_id}-${sb.belief_id}-${sb.supporting_belief_id}`)
        )
      );
    }
  };

  const getPositionColor = (position: string): string => {
    switch (position) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const handleNextPage = (): void => {
    if (supportingBeliefs.length === itemsPerPage) {
      const lastItem = supportingBeliefs[supportingBeliefs.length - 1];
      const cursor =
        orderBy === 'createdAt'
          ? lastItem.createdAt.toISOString()
          : lastItem.updatedAt.toISOString();
      fetchSupportingBeliefs('next', cursor);
    }
  };

  const handlePreviousPage = (): void => {
    if (currentPage > 1) {
      const previousPage = pageHistory[pageHistory.length - 2];
      fetchSupportingBeliefs('previous', previousPage?.cursor || undefined);
    }
  };

  const handleOrderByChange = (newOrderBy: string): void => {
    setOrderBy(newOrderBy);
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
  };

  const handleOrderDirectionChange = (newDirection: string): void => {
    setOrderDirection(newDirection);
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
  };

  const handleRowClick = (
    supportingBelief: ClientBeliefSupportWithRelations,
    event: React.MouseEvent
  ): void => {
    // Don't navigate if clicking on checkbox or action buttons
    const target = event.target as HTMLElement;
    if (
      (target as HTMLInputElement).type === 'checkbox' ||
      target.closest('.action-btn') ||
      target.closest('.user-link')
    ) {
      return;
    }

    // Navigate to belief detail view - prefer the main belief
    navigate(`/users/${supportingBelief.user_id}/belief/${supportingBelief.belief_id}`);
  };

  // Calculate total pages estimate
  const totalPages = Math.max(1, Math.ceil(totalSupportingBeliefs / itemsPerPage));

  // Generate unique key for supporting belief
  const getSupportingBeliefKey = (sb: ClientBeliefSupportWithRelations): string => {
    return `${sb.user_id}-${sb.belief_id}-${sb.supporting_belief_id}`;
  };

  return (
    <div className={`supporting-beliefs-list-view ${compact ? 'compact' : ''}`}>
      {title && (
        <div className="list-view-header">
          <h3>{title}</h3>
        </div>
      )}

      {!compact && (
        <div className="list-view-controls">
          <div className="controls-row">
            <div className="order-controls">
              <label>Order by:</label>
              <select value={orderBy} onChange={(e) => handleOrderByChange(e.target.value)}>
                <option value="createdAt">Created Date</option>
                <option value="updatedAt">Updated Date</option>
              </select>
              <button
                className="order-direction-btn"
                onClick={() =>
                  handleOrderDirectionChange(orderDirection === 'DESC' ? 'ASC' : 'DESC')
                }
              >
                {orderDirection === 'DESC' ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {showBulkActions && selectedSupportingBeliefs.size > 0 && (
              <div className="bulk-actions">
                <span className="selected-count">{selectedSupportingBeliefs.size} selected</span>
                <button className="btn-secondary">Bulk Action</button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="supporting-beliefs-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading supporting beliefs...</div>
        ) : (
          <>
            {supportingBeliefs.length === 0 ? (
              <div className="no-results">
                <p>No supporting beliefs found matching your criteria.</p>
              </div>
            ) : (
              <>
                <div className="supporting-beliefs-table-container">
                  <table className="supporting-beliefs-table">
                    <thead>
                      <tr>
                        {showCheckboxes && (
                          <th>
                            <input
                              type="checkbox"
                              checked={
                                selectedSupportingBeliefs.size === supportingBeliefs.length &&
                                supportingBeliefs.length > 0
                              }
                              onChange={handleSelectAll}
                            />
                          </th>
                        )}
                        <th>User ID</th>
                        <th>Belief ID</th>
                        <th>Supporting Belief ID</th>
                        <th>Position</th>
                        <th>Supporting Position</th>
                        <th>Belief Text</th>
                        <th>Supporting Text</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportingBeliefs.map((supportingBelief) => {
                        const key = getSupportingBeliefKey(supportingBelief);
                        return (
                          <tr
                            key={key}
                            className={`supporting-belief-row ${
                              selectedSupportingBeliefs.has(key) ? 'selected' : ''
                            }`}
                            onClick={(e) => handleRowClick(supportingBelief, e)}
                          >
                            {showCheckboxes && (
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedSupportingBeliefs.has(key)}
                                  onChange={() => handleSelectSupportingBelief(key)}
                                />
                              </td>
                            )}
                            <td>
                              <button
                                className="user-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/users/${supportingBelief.user_id}`);
                                }}
                              >
                                <User size={14} />
                                {supportingBelief.user_id}
                              </button>
                            </td>
                            <td>
                              <button
                                className="belief-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/users/${supportingBelief.user_id}/belief/${supportingBelief.belief_id}`
                                  );
                                }}
                              >
                                <Shield size={14} />
                                {supportingBelief.belief_id}
                              </button>
                            </td>
                            <td>
                              <button
                                className="belief-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/users/${supportingBelief.user_id}/belief/${supportingBelief.supporting_belief_id}`
                                  );
                                }}
                              >
                                <Shield size={14} />
                                {supportingBelief.supporting_belief_id}
                              </button>
                            </td>
                            <td>
                              <span
                                className={`position-badge ${getPositionColor(
                                  supportingBelief.cache_position
                                )}`}
                              >
                                {supportingBelief.cache_position.toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`position-badge ${getPositionColor(
                                  supportingBelief.cache_supportingposition
                                )}`}
                              >
                                {supportingBelief.cache_supportingposition.toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div className="thought-text">
                                {supportingBelief.belief?.thought?.text || 'No thought text'}
                              </div>
                            </td>
                            <td>
                              <div className="thought-text">
                                {supportingBelief.supporting_belief?.thought?.text ||
                                  'No thought text'}
                              </div>
                            </td>
                            <td>{formatDate(supportingBelief.createdAt)}</td>
                            <td>
                              <div className="actions">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/users/${supportingBelief.user_id}/belief/${supportingBelief.belief_id}`
                                    );
                                  }}
                                  className="action-btn"
                                  title="View Main Belief"
                                >
                                  <Shield size={14} />
                                </button>
                                <button className="action-btn" title="View Details">
                                  <Eye size={14} />
                                </button>
                                <button className="action-btn" title="More Options">
                                  <MoreVertical size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="pagination">
                  <div className="pagination-info">
                    Showing {supportingBeliefs.length} supporting beliefs
                    {totalSupportingBeliefs > 0 && <span> of {totalSupportingBeliefs} total</span>}
                  </div>
                  <div className="pagination-controls">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="btn-secondary"
                    >
                      Previous
                    </button>
                    <span className="page-info">
                      Page {currentPage}
                      {totalSupportingBeliefs > 0 && <span> of {totalPages}</span>}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={supportingBeliefs.length < itemsPerPage}
                      className="btn-secondary"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportingBeliefsListView;
